import React, { useState, useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { createRolePermission, updateRolePermission, fetchRolePermissionsByRoleId, deleteRolePermission } from '../../../core/data/redux/user/rolePermission/rolePermissionAction';
import { AppDispatch } from '../../../core/data/redux/store';
import { useDispatch, useSelector } from 'react-redux';
import { SidebarDataTest } from '../../../core/data/json/sidebarMenu';
import { message, Typography, Tag, Popconfirm } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { PLAN_FEATURES } from './planFeatureMap';

const { Text } = Typography;

interface selectAllColumn {
    AllowAll: boolean;
    Add: boolean;
    Edit: boolean;
    Delete: boolean;
    View: boolean;
    Import: boolean;
    Export: boolean;
}
type PermissionKey = 'AllowAll' | 'Add' | 'Edit' | 'Delete' | 'View' | 'Import' | 'Export';

const filterSidebarByPlan = (sidebar: any[], allowedFeatures: string[] | null) => {
    if (!allowedFeatures) return sidebar;

    const lowerAllowed = allowedFeatures.map(f => f.toLowerCase().trim());
    if (lowerAllowed.includes('*')) return sidebar;

    const filterNode = (node: any) => {
        const nodeLabel = (node.label || '').toLowerCase();

        const newNode: any = { ...node };

        if (Array.isArray(node.submenuItems) && node.submenuItems.length > 0) {
            const filteredChildren = node.submenuItems
                .map(filterNode)
                .filter(Boolean);
            if (filteredChildren.length > 0) {
                newNode.submenuItems = filteredChildren;
                return newNode;
            }
        }

        if (lowerAllowed.includes(nodeLabel)) {
            newNode.submenuItems = node.submenuItems ? [] : undefined;
            return newNode;
        }

        return null;
    };

    return sidebar
        .map(filterNode)
        .filter(Boolean) as any[];
};

const PermissionPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { roleId } = useParams<{ roleId: string }>();
    const parsedRoleId = roleId ? Number(roleId) : NaN;

    const [expandedParents, setExpandedParents] = useState<Record<string, boolean>>({});
    // these will be initialized after deriving initialPermissions/initialSelectAllModules
    const [permissions, setPermissions] = useState<any>({});
    const [selectAllModules, setSelectAllModules] = useState<any>({});
    const { rolePermissions } = useSelector((state: any) => state.rolePermission);
    const { user } = useSelector((state: any) => state.auth);
    const [isSaving, setIsSaving] = useState(false);
    const [subscriptionData, setSubscriptionData] = useState<any>(null);
    const [filteredSidebar, setFilteredSidebar] = useState<any[]>(SidebarDataTest);
    const [selectAllColumn, setSelectAllColumn] = useState<selectAllColumn>({
        AllowAll: false,
        Add: false,
        Edit: false,
        Delete: false,
        View: false,
        Import: false,
        Export: false,
    });

    // Derive filteredSidebar whenever subscriptionData changes
    useEffect(() => {
        const planName = subscriptionData?.plan_name || subscriptionData?.plan || null;

        if (!planName) {
            setFilteredSidebar(SidebarDataTest);
            return;
        }

        const planKey = planName.toString().trim().toLowerCase();
        let allowedFeatures = PLAN_FEATURES[planKey];
        if (!allowedFeatures) {
            allowedFeatures = PLAN_FEATURES[planKey.toLowerCase()] || null;
        }

        const newFiltered = filterSidebarByPlan(SidebarDataTest, allowedFeatures || null);
        setFilteredSidebar(newFiltered);
    }, [subscriptionData]);

    // compute labels, maps, initial perms from filteredSidebar
    const {
        labels,
        allLabels,
        parentToChildrenMap,
        childToParentMap,
        initialSelectAllModules,
        initialPermissions
    } = useMemo(() => {
        const labelsArr: string[] = (filteredSidebar || []).flatMap((menu: any) =>
            (menu.submenuItems || []).flatMap((item: any) => {
                const childLabels = (item.submenuItems || []).flatMap((child: any) => {
                    const subChildLabels = (child.submenuItems || []).map((subChild: any) => subChild.label) || [];
                    return [child.label, ...subChildLabels];
                }) || [];
                return [item.label, ...childLabels];
            })
        );

        const allLabelsArr: string[] = (filteredSidebar || []).flatMap(menu =>
            (menu.submenuItems || []).flatMap((item: any) => [
                item.label,
                ...((item.submenuItems || []).flatMap((child: any) => [
                    child.label,
                    ...((child.submenuItems || []).map((subChild: any) => subChild.label) || [])
                ]) || [])
            ])
        );

        const p2c: Record<string, string[]> = {};
        const c2p: Record<string, string> = {};

        (filteredSidebar || []).forEach(menu => {
            (menu.submenuItems || []).forEach((parent: any) => {
                p2c[parent.label] = (parent.submenuItems || []).map((child: any) => child.label) || [];
                (parent.submenuItems || []).forEach((child: any) => {
                    p2c[child.label] = (child.submenuItems || []).map((subChild: any) => subChild.label) || [];

                    // immediate parent mapping
                    c2p[child.label] = parent.label;

                    (child.submenuItems || []).forEach((subChild: any) => {
                        c2p[subChild.label] = child.label;
                    });
                });
            });
        });

        const initSelectAll = Object.fromEntries(labelsArr.map((label: string) => [label, false]));

        const initPerms = Object.fromEntries(
            allLabelsArr.map((label: string) => [
                label,
                {
                    AllowAll: false,
                    Add: false,
                    Edit: false,
                    Delete: false,
                    View: false,
                    Import: false,
                    Export: false,
                },
            ])
        );

        return {
            labels: labelsArr,
            allLabels: allLabelsArr,
            parentToChildrenMap: p2c,
            childToParentMap: c2p,
            initialSelectAllModules: initSelectAll,
            initialPermissions: initPerms
        };
    }, [filteredSidebar]);
    useEffect(() => {
        setSelectAllModules(initialSelectAllModules);
        setPermissions(initialPermissions);
        setSelectAllColumn({
            AllowAll: false,
            Add: false,
            Edit: false,
            Delete: false,
            View: false,
            Import: false,
            Export: false,
        });
    }, [initialSelectAllModules, initialPermissions]);

    // fetch subscription details
    const fetchSubscriptionDetails = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_AUTH_SERVICE_SERVER_BASE_URL}/auth/subscription-detail`, {
                method: "GET",
                credentials: "include",
            });
            const result = await response.json();
            if (result.status === "success") {
                setSubscriptionData(result.data);
            } else {
                message.error("Failed to fetch subscription details");
            }
        } catch (error) {
            console.error("Error fetching subscription details:", error);
            message.error("Error loading subscription details");
        }
    };

    useEffect(() => {
        fetchSubscriptionDetails();
    }, [dispatch]);

    // fetch role permissions
    useEffect(() => {
        if (parsedRoleId) {
            dispatch(fetchRolePermissionsByRoleId(parsedRoleId));
        }
    }, [dispatch, parsedRoleId]);

    // whenever the rolePermissions from store change, map them into our permissions state
    useEffect(() => {
        if (!rolePermissions || rolePermissions.length === 0) {
            setPermissions({ ...initialPermissions });
            return;
        }

        // Start fresh with initialPermissions so keys match filtered menu
        const updatedPermissions: any = { ...initialPermissions };

        rolePermissions.forEach((permission: any) => {
            const { PermissionKey, PermissionValues } = permission;
            if (updatedPermissions[PermissionKey]) {
                // reset to defaults
                updatedPermissions[PermissionKey] = {
                    AllowAll: false,
                    Add: false,
                    Edit: false,
                    Delete: false,
                    View: false,
                    Import: false,
                    Export: false,
                };

                updatedPermissions[PermissionKey].AllowAll = PermissionValues?.includes('AllowAll');
                (PermissionValues || []).forEach((perm: string) => {
                    if (updatedPermissions[PermissionKey][perm] !== undefined) {
                        updatedPermissions[PermissionKey][perm] = true;
                    }
                });
            } else {
                // If PermissionKey isn't in initialPermissions (maybe due to plan filter),
                // we ignore it to avoid adding permissions for non-allowed modules.
            }
        });

        setPermissions(updatedPermissions);
    }, [rolePermissions, initialPermissions]);

    // keep selectAllModules in-sync with permissions
    useEffect(() => {
        const updatedSelectAll = { ...selectAllModules };
        for (const module in permissions) {
            const perms = permissions[module];
            const hasAnyPermission = Object.values(perms).some(val => val);
            updatedSelectAll[module] = hasAnyPermission;
        }
        setSelectAllModules(updatedSelectAll);
    }, [permissions]);

    // helper: select parent chain (makes parent visible if child is selected)
    const selectParentChain = (label: string, updatedSelectAll: any, updatedPermissions: any) => {
        const parent = childToParentMap[label];
        if (parent) {
            updatedSelectAll[parent] = true;
            if (updatedPermissions[parent]) {
                updatedPermissions[parent].View = true;
            }
            selectParentChain(parent, updatedSelectAll, updatedPermissions);
        }
    };

    const toggleExpand = (label: string) => {
        setExpandedParents(prev => ({
            ...prev,
            [label]: !prev[label],
        }));
    };

    const handleModuleSelectChange = (module: string) => {
        const newValue = !selectAllModules[module];
        setSelectAllModules((prevState: any) => {
            const updatedState = { ...prevState };
            const updatedPermissions = { ...permissions };

            const applyToAllDescendants = (label: string) => {
                updatedState[label] = newValue;

                updatedPermissions[label] = newValue
                    ? {
                        View: true,
                        Add: false,
                        Edit: false,
                        Delete: false,
                        Import: false,
                        Export: false,
                        AllowAll: false,
                    }
                    : {
                        View: false,
                        Add: false,
                        Edit: false,
                        Delete: false,
                        Import: false,
                        Export: false,
                        AllowAll: false,
                    };

                const children = parentToChildrenMap[label];
                if (children) {
                    children.forEach(child => applyToAllDescendants(child));
                }
            };

            applyToAllDescendants(module);

            if (newValue) {
                selectParentChain(module, updatedState, updatedPermissions);
            }

            setPermissions(updatedPermissions);
            return updatedState;
        });
    };

    const handleCheckboxChange = (module: string, permission: string) => {
        setPermissions((prev: any) => {
            const updatedPermissions = { ...prev };
            const updatedSelectAll = { ...selectAllModules };

            const togglePermission = (label: string, value: boolean) => {
                // ensure module exists (could be absent if filtered)
                if (!updatedPermissions[label]) {
                    updatedPermissions[label] = {
                        AllowAll: false,
                        Add: false,
                        Edit: false,
                        Delete: false,
                        View: false,
                        Import: false,
                        Export: false,
                    };
                }
                const updatedModulePermissions = { ...updatedPermissions[label] };

                if (permission === 'AllowAll') {
                    updatedPermissions[label] = {
                        AllowAll: value,
                        View: false,
                        Add: false,
                        Edit: false,
                        Delete: false,
                        Import: false,
                        Export: false,
                    };
                } else {
                    updatedModulePermissions[permission] = value;
                    updatedPermissions[label] = updatedModulePermissions;
                }

                updatedSelectAll[label] = Object.values(updatedPermissions[label]).some(val => val);
            };

            const applyToAllDescendants = (label: string, value: boolean) => {
                togglePermission(label, value);
                const children = parentToChildrenMap[label];
                if (children) {
                    children.forEach(child => applyToAllDescendants(child, value));
                }
            };

            const isNowChecked = !((updatedPermissions[module] && updatedPermissions[module][permission]) || false);
            applyToAllDescendants(module, isNowChecked);

            if (isNowChecked) {
                selectParentChain(module, updatedSelectAll, updatedPermissions);
            }

            setSelectAllModules(updatedSelectAll);
            return updatedPermissions;
        });
    };

    const PERMISSION_KEYS: PermissionKey[] = [
        'AllowAll', 'Add', 'Edit', 'Delete', 'View', 'Import', 'Export'
    ];
    const NON_ALLOW_KEYS: PermissionKey[] = [
        'Add', 'Edit', 'Delete', 'View', 'Import', 'Export'
    ];

    const handleSelectAllColumn = (permission: PermissionKey) => {
        const toggledValue = !selectAllColumn[permission];

        if (permission === 'AllowAll') {
            setSelectAllColumn({
                AllowAll: toggledValue,
                Add: false,
                Edit: false,
                Delete: false,
                View: false,
                Import: false,
                Export: false,
            });

            const updated = { ...permissions };
            Object.keys(updated).forEach((module) => {
                if (selectAllModules[module]) {
                    if (!updated[module]) {
                        updated[module] = initialPermissions[module]
                            ? { ...initialPermissions[module] }
                            : {
                                AllowAll: false,
                                Add: false,
                                Edit: false,
                                Delete: false,
                                View: false,
                                Import: false,
                                Export: false,
                            };
                    }
                    updated[module].AllowAll = toggledValue;
                    if (toggledValue) {
                        NON_ALLOW_KEYS.forEach((k) => {
                            updated[module][k] = false;
                        });
                    }
                }
            });
            setPermissions(updated);
            return;
        }

        if (selectAllColumn.AllowAll) return;

        setSelectAllColumn((prev) => ({ ...prev, [permission]: toggledValue }));

        const updated = { ...permissions };
        Object.keys(updated).forEach((module) => {
            if (selectAllModules[module] && !updated[module].AllowAll) {
                updated[module][permission] = toggledValue;
            }
        });
        setPermissions(updated);
    };

    const handleSelectAllModules = (type: string) => {
        if (type === "All") {
            const newValue = !selectAllModules.All;

            setSelectAllModules((prev: any) => {
                const next = { ...prev, All: newValue };
                labels.forEach(label => {
                    next[label] = newValue;
                });
                return next;
            });

            setPermissions((prev: any) => {
                const updatedPermissions = { ...prev };
                Object.keys(updatedPermissions).forEach((module) => {
                    if (newValue) {
                        updatedPermissions[module] = {
                            ...updatedPermissions[module],
                            View: true,
                        };
                    } else {
                        updatedPermissions[module] = {
                            AllowAll: false,
                            Add: false,
                            Edit: false,
                            Delete: false,
                            View: false,
                            Import: false,
                            Export: false,
                        };
                    }
                });
                return updatedPermissions;
            });
        }
    };

    const handleSubmit = async () => {
        setIsSaving(true);
        try {
            const permissionValues = Object.keys(permissions)
                .map(module => {
                    const perms = permissions[module];
                    return {
                        module,
                        permissions: Object.keys(perms)
                            .filter(permission => perms[permission])
                            .map(permission => permission)
                    };
                })
                .filter(permission => permission.permissions.length > 0);
            const selectedModules = permissionValues.map(p => p.module);
            for (const permission of permissionValues) {
                const rolePermissionData = {
                    RoleId: parsedRoleId,
                    PermissionKey: permission.module,
                    PermissionValues: permission.permissions,
                    CreatedBy: user.UserId,
                    ModifiedBy: user.UserId,
                };

                const existingPermission = rolePermissions.find(
                    (perm: any) =>
                        perm.RoleId === parsedRoleId &&
                        perm.PermissionKey === permission.module
                );

                if (existingPermission) {
                    await dispatch(
                        updateRolePermission({
                            RolePermissionId: existingPermission.RolePermissionId,
                            ...rolePermissionData,
                        })
                    );
                } else {
                    await dispatch(createRolePermission(rolePermissionData));
                }
            }

            // Delete unselected permissions
            for (const existingPermission of rolePermissions) {
                if (!selectedModules.includes(existingPermission.PermissionKey)) {
                    await dispatch(deleteRolePermission(existingPermission.RolePermissionId));
                }
            }

            message.success("Permissions updated successfully");
            if (parsedRoleId) {
                dispatch(fetchRolePermissionsByRoleId(parsedRoleId));
            }
        } catch (error: any) {
            console.error(error);
            message.error('Error updating role permissions');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeletePermission = async (moduleLabel: string) => {
        try {
            const existingPermission = rolePermissions.find(
                (perm: any) =>
                    perm.RoleId === parsedRoleId &&
                    perm.PermissionKey === moduleLabel
            );
            if (existingPermission) {
                await dispatch(deleteRolePermission(existingPermission.RolePermissionId));
                message.success(`Deleted permissions for module: ${moduleLabel}`);

                if (parsedRoleId) {
                    dispatch(fetchRolePermissionsByRoleId(parsedRoleId));
                }

                setPermissions((prev: any) => ({
                    ...prev,
                    [moduleLabel]: {
                        AllowAll: false,
                        Add: false,
                        Edit: false,
                        Delete: false,
                        View: false,
                        Import: false,
                        Export: false,
                    },
                }));
                setSelectAllModules((prev: any) => ({
                    ...prev,
                    [moduleLabel]: false,
                }));
            } else {
                message.warning('No permission found to delete.');
            }
        } catch (error: any) {
            message.error(`Error deleting permission for ${moduleLabel}`);
        }
    };

    // Render
    return (
        <>
            <div className="page-wrapper">
                <div className="content">
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Permissions</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to="#">Dashboard</Link>
                                    </li>
                                    <li className="breadcrumb-item">Administration</li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Permissions
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <button
                            type="button"
                            className="btn btn-primary mb-2"
                            onClick={handleSubmit}
                            disabled={isSaving}
                        >
                            {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                            Save Permissions
                        </button>
                    </div>

                    <div className="card">
                        <div className="card-header d-flex justify-content-between align-items-center flex-wrap">
                            <div>
                                <Text strong>
                                    Role Name :{' '}
                                    <Tag color="green">{rolePermissions[0]?.RoleName || 'N/A'}</Tag>
                                </Text>
                            </div>
                            <div>
                                <Text strong>
                                    Last Updated By :{' '}
                                    <Tag color="blue">{rolePermissions[0]?.ModifiedByUsername || 'N/A'}</Tag>
                                </Text>
                            </div>
                            <div>
                                <Text strong>
                                    Last Modified At :{' '}
                                    <Tag color="geekblue">
                                        {rolePermissions[0]?.ModifiedAt
                                            ? new Date(rolePermissions[0]?.ModifiedAt).toLocaleString()
                                            : 'N/A'}
                                    </Tag>
                                </Text>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table">
                                    <thead className="thead-light">
                                        <tr>
                                            <th className="no-sort">
                                                Modules
                                                <input
                                                    type="checkbox"
                                                    className="ms-2"
                                                    checked={labels && labels.length > 0 ? labels.every(l => !!selectAllModules[l]) : false}
                                                    onChange={() => handleSelectAllModules("All")}
                                                />
                                            </th>
                                            <th>
                                                Allow All
                                                <input
                                                    type="checkbox"
                                                    className="ms-2"
                                                    checked={selectAllColumn.AllowAll}
                                                    onChange={() => handleSelectAllColumn('AllowAll')}
                                                />
                                            </th>
                                            <th>
                                                Add
                                                <input
                                                    type="checkbox"
                                                    className="ms-2"
                                                    checked={selectAllColumn.Add}
                                                    onChange={() => handleSelectAllColumn('Add')}
                                                    disabled={selectAllColumn.AllowAll}
                                                />
                                            </th>
                                            <th>
                                                Edit
                                                <input
                                                    type="checkbox"
                                                    className="ms-2"
                                                    checked={selectAllColumn.Edit}
                                                    onChange={() => handleSelectAllColumn('Edit')}
                                                    disabled={selectAllColumn.AllowAll}
                                                />
                                            </th>
                                            <th>
                                                Delete
                                                <input
                                                    type="checkbox"
                                                    className="ms-2"
                                                    checked={selectAllColumn.Delete}
                                                    onChange={() => handleSelectAllColumn('Delete')}
                                                    disabled={selectAllColumn.AllowAll}
                                                />
                                            </th>
                                            <th>
                                                View
                                                <input
                                                    type="checkbox"
                                                    className="ms-2"
                                                    checked={selectAllColumn.View}
                                                    onChange={() => handleSelectAllColumn('View')}
                                                    disabled={selectAllColumn.AllowAll}
                                                />
                                            </th>
                                            <th>
                                                Import
                                                <input
                                                    type="checkbox"
                                                    className="ms-2"
                                                    checked={selectAllColumn.Import}
                                                    onChange={() => handleSelectAllColumn('Import')}
                                                    disabled={selectAllColumn.AllowAll}
                                                />
                                            </th>
                                            <th>
                                                Export
                                                <input
                                                    type="checkbox"
                                                    className="ms-2"
                                                    checked={selectAllColumn.Export}
                                                    onChange={() => handleSelectAllColumn('Export')}
                                                    disabled={selectAllColumn.AllowAll}
                                                />
                                            </th>
                                            <th>Action</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {filteredSidebar.flatMap((menu: any) =>
                                            (menu.submenuItems || []).map((parent: any) => (
                                                <React.Fragment key={parent.label}>
                                                    <tr className="bg-light">
                                                        <td className="fw-bold text-gray-9">
                                                            <button
                                                                className="btn btn-sm  p-0 me-2"
                                                                onClick={() => toggleExpand(parent.label)}
                                                            >
                                                                {expandedParents[parent.label] ? '▼' : '▶'}
                                                            </button>

                                                            <input type="checkbox"
                                                                checked={!!selectAllModules[parent.label]}
                                                                onChange={() => handleModuleSelectChange(parent.label)}
                                                            />
                                                            {" "}{parent.label}
                                                        </td>
                                                        <td>
                                                            <div className="form-check form-check-md">
                                                                <input
                                                                    className="form-check-input fs-4 border-2 bg-success"
                                                                    type="checkbox"
                                                                    checked={!!permissions[parent.label]?.AllowAll}
                                                                    onChange={() => handleCheckboxChange(parent.label, 'AllowAll')}
                                                                    disabled={!selectAllModules[parent.label]}
                                                                />
                                                            </div>
                                                        </td>
                                                        {['Add', 'Edit', 'Delete', 'View', 'Import', 'Export'].map((permission) => (
                                                            <td key={permission}>
                                                                <div className="form-check form-check-md">
                                                                    <input
                                                                        className="form-check-input fs-4 border-2 bg-secondary"
                                                                        type="checkbox"
                                                                        checked={!!(permissions[parent.label] && permissions[parent.label][permission])}
                                                                        onChange={() => handleCheckboxChange(parent.label, permission)}
                                                                        disabled={!selectAllModules[parent.label] || !!permissions[parent.label]?.AllowAll}
                                                                    />
                                                                </div>
                                                            </td>
                                                        ))}
                                                        <td>
                                                            <Popconfirm
                                                                title={`Are you sure you want to delete permissions for "${parent.label}"?`}
                                                                onConfirm={() => handleDeletePermission(parent.label)}
                                                                okText="Yes"
                                                                cancelText="No"
                                                                placement="topRight"
                                                            >
                                                                <button className="btn btn-sm btn-danger">
                                                                    <DeleteOutlined />
                                                                </button>
                                                            </Popconfirm>
                                                        </td>
                                                    </tr>

                                                    {expandedParents[parent.label] && (
                                                        <>
                                                            {(parent.submenuItems || []).map((child: any) => (
                                                                <React.Fragment key={child.label}>
                                                                    <tr>
                                                                        <td className="ps-5 text-gray-9">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={!!selectAllModules[child.label]}
                                                                                onChange={() => handleModuleSelectChange(child.label)}
                                                                            />
                                                                            {" "}{child.label}
                                                                        </td>
                                                                        <td>
                                                                            <div className="form-check form-check-md">
                                                                                <input
                                                                                    className="form-check-input fs-4 border-2 bg-success"
                                                                                    type="checkbox"
                                                                                    checked={!!permissions[child.label]?.AllowAll}
                                                                                    onChange={() => handleCheckboxChange(child.label, 'AllowAll')}
                                                                                    disabled={!selectAllModules[child.label]}
                                                                                />
                                                                            </div>
                                                                        </td>
                                                                        {['Add', 'Edit', 'Delete', 'View', 'Import', 'Export'].map((permission) => (
                                                                            <td key={permission}>
                                                                                <div className="form-check form-check-md">
                                                                                    <input
                                                                                        className="form-check-input fs-4 border-2 bg-secondary"
                                                                                        type="checkbox"
                                                                                        checked={!!(permissions[child.label] && permissions[child.label][permission])}
                                                                                        onChange={() => handleCheckboxChange(child.label, permission)}
                                                                                        disabled={!selectAllModules[child.label] || !!permissions[child.label]?.AllowAll}
                                                                                    />
                                                                                </div>
                                                                            </td>
                                                                        ))}
                                                                        <td>
                                                                            <Popconfirm
                                                                                title={`Are you sure you want to delete permissions for "${child.label}"?`}
                                                                                onConfirm={() => handleDeletePermission(child.label)}
                                                                                okText="Yes"
                                                                                cancelText="No"
                                                                                placement="topRight"
                                                                            >
                                                                                <button className="btn btn-sm btn-danger">
                                                                                    <DeleteOutlined />
                                                                                </button>
                                                                            </Popconfirm>
                                                                        </td>
                                                                    </tr>

                                                                    {Array.isArray(child.submenuItems) &&
                                                                        child.submenuItems.map((subChild: any) => (
                                                                            <tr key={subChild.label}>
                                                                                <td className="text-gray-9  ms-3" style={{ paddingLeft: "70px" }}>
                                                                                    <input
                                                                                        type="checkbox"
                                                                                        checked={!!selectAllModules[subChild.label]}
                                                                                        onChange={() => handleModuleSelectChange(subChild.label)}
                                                                                    />
                                                                                    {" "}{subChild.label}
                                                                                </td>
                                                                                <td>
                                                                                    <div className="form-check form-check-md">
                                                                                        <input
                                                                                            className="form-check-input fs-4 border-2 bg-success"
                                                                                            type="checkbox"
                                                                                            checked={!!permissions[subChild.label]?.AllowAll}
                                                                                            onChange={() => handleCheckboxChange(subChild.label, 'AllowAll')}
                                                                                            disabled={!selectAllModules[subChild.label]}
                                                                                        />
                                                                                    </div>
                                                                                </td>
                                                                                {['Add', 'Edit', 'Delete', 'View', 'Import', 'Export'].map((permission) => (
                                                                                    <td key={permission}>
                                                                                        <div className="form-check form-check-md">
                                                                                            <input
                                                                                                className="form-check-input fs-4 border-2 bg-secondary"
                                                                                                type="checkbox"
                                                                                                checked={!!(permissions[subChild.label] && permissions[subChild.label][permission])}
                                                                                                onChange={() => handleCheckboxChange(subChild.label, permission)}
                                                                                                disabled={!selectAllModules[subChild.label] || !!permissions[subChild.label]?.AllowAll}
                                                                                            />
                                                                                        </div>
                                                                                    </td>
                                                                                ))}
                                                                                <td>
                                                                                    <Popconfirm
                                                                                        title={`Are you sure you want to delete permissions for "${subChild.label}"?`}
                                                                                        onConfirm={() => handleDeletePermission(subChild.label)}
                                                                                        okText="Yes"
                                                                                        cancelText="No"
                                                                                        placement="topRight"
                                                                                    >
                                                                                        <button className="btn btn-sm btn-danger">
                                                                                            <DeleteOutlined />
                                                                                        </button>
                                                                                    </Popconfirm>
                                                                                </td>
                                                                            </tr>
                                                                        ))}
                                                                </React.Fragment>
                                                            ))}
                                                        </>
                                                    )}
                                                </React.Fragment>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <button
                        type="button"
                        className="btn btn-primary mb-2"
                        onClick={handleSubmit}
                        disabled={isSaving}
                    >
                        {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>}
                        Save Permissions
                    </button>
                </div>

                <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
                    <p className="mb-0">2024 - 2025© hrmtree.</p>
                    <p>
                        Developed By{' '}
                        <Link to="#" className="text-primary">
                            Leadchain Systems
                        </Link>
                    </p>
                </div>
            </div>
        </>
    );
};

export default PermissionPage;
